import { OnInit, OnDestroy, ChangeDetectionStrategy, Component, ChangeDetectorRef, ViewChild } from "@angular/core";
import { combineLatest, EMPTY, Subject } from "rxjs";
import { switchMap, catchError, finalize, takeUntil } from "rxjs/operators";
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexGrid,
    ApexStroke,
    ApexTitleSubtitle,
    ApexXAxis,
    ChartComponent,
} from "ng-apexcharts";
import { GetPrescriptionsStatsRequest, PrescriptionApi, PrescriptionHistoryItem } from "../api/proxies";
import { StatsService } from "../services/stats.service";
import { NotifyService } from "@ui/notify";

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    dataLabels: ApexDataLabels;
    grid: ApexGrid;
    stroke: ApexStroke;
    title: ApexTitleSubtitle;
};

@Component({
    selector: "prescription-history",
    templateUrl: "./prescription-history.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionHistoryComponent implements OnInit, OnDestroy {
    private _destroy$ = new Subject<void>();

    @ViewChild("chart") chart: ChartComponent;

    public chartOptions!: Partial<ChartOptions>;
    public total: number = 0;
    public loading = false;

    constructor(
        private prescriptionApi: PrescriptionApi,
        private statsService: StatsService,
        private _changeDetector: ChangeDetectorRef,
        private _notifyService: NotifyService
    ) {}

    ngOnInit(): void {
        const stream$ = combineLatest([this.statsService.period$, this.statsService._tenant$]).pipe(
            takeUntil(this._destroy$),
            switchMap(([period, tenant]) => {
                this.loading = true;
                const req = new GetPrescriptionsStatsRequest();
                req.start = period.periodStart;
                req.end = period.periodEnd;
                req.tenantId = tenant?.id;
                return this.prescriptionApi.history(req).pipe(
                    catchError((err) => {
                        this._notifyService.error("Falha ao carregar histórico de prescrições.");
                        return EMPTY;
                    }),
                    finalize(() => {
                        this.loading = false;
                        this._changeDetector.markForCheck();
                    })
                );
            })
        );

        stream$.subscribe((result: PrescriptionHistoryItem[]) => {
            const series: ApexAxisChartSeries = [
                { name: "Inteligente", data: result.map((i) => i.smart), color: "#50992d" },
                { name: "Padrão", data: result.map((i) => i.standard), color: "#4587ec" },
            ];

            this.total = series.reduce((acc, s) => acc + (s.data as number[]).reduce((sum, v) => sum + v, 0), 0);

            this.chartOptions = {
                series,
                chart: {
                    height: 350,
                    type: "bar",
                    toolbar: { show: false },
                },
                dataLabels: { enabled: true },
                stroke: { curve: "smooth" },
                xaxis: {
                    categories: result.map((i) => i.date.toLocaleDateString()),
                },
                grid: { show: false },
            };

            this._changeDetector.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
